import { Box, Button, Chip, Container, Grid, MenuItem, Select, Stack, styled, Typography } from "@mui/material";
import { FC } from "react";
import Cover from "../../Misc/Cover";

import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "react-i18next";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { PlayPause } from "../../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch } from "../../../Redux/redux-store";
import { hexToRgbString } from "../../../Utils/Helpers/cssUtils";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import { Distinct } from "../../../Utils/helpers";
import { PlayingStatus } from "../../MediaPlayer/ReactPlayerWrapper/playing-statuses";

export type TitleHeroProps = {
  id: number;
  cover?: string;
  title?: string;
  description?: string;
  casting?: string[];
  content?: string;
  tags?: string[];
  seasons?: string[];
  genre?: string[];
  type?: "serie" | "movie";
};

const TitleHero: FC<TitleHeroProps> = (props) => {
  const { t } = useTranslation();
  const isSerieTitle = props.type === "serie";
  const dispatch = useAppDispatch();
  const { addObjectByIdToBasket } = useBasketActions();
  const { AddItemsToCollection } = useCollectionActions();

  return (
    <Hero>
      <Background>{<Cover img={{ src: props.cover }} />}</Background>
      <Container sx={{ pt: 12, pb: 3 }}>
        <Grid container>
          <Grid item md={6} display="flex" flexDirection="column">
            {isSerieTitle && props.seasons && (
              <Stack alignItems="flex-start">
                <Select
                  IconComponent={ExpandMoreRoundedIcon}
                  value={props.seasons[3] || ""}
                  sx={{ mb: 1 }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                >
                  {props.seasons.map((season) => (
                    <MenuItem key={season} value={season}>
                      {season}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            )}

            <Typography variant="h1" sx={{ mb: 2, mt: "auto" }}>
              {props.title}
            </Typography>
            <Stack direction="row" spacing={1} mb={3}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => addObjectByIdToBasket(props.id!)}
              >
                {t("button:addToOrder")}
              </Button>
              <Button
                variant="icon"
                onClick={() => AddItemsToCollection([new DbAssetObjectInfos({ id: props.id, name: props.title! })])}
              >
                <BookmarkBorderOutlinedIcon />
              </Button>
              <Button variant="icon" onClick={() => dispatch(PlayPause())}>
                <PlayingStatus />
              </Button>
            </Stack>
            {(props.type || props.genre) && (
              <Typography component="p" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
                {props.type}, {props.genre?.join(", ")}
              </Typography>
            )}
            {props.casting && (
              <Typography component="p" color="text.secondary" fontWeight={400} sx={{ mb: 1.5 }}>
                {t("common:with")} {""}
                <Typography component="span" color="text.primary" fontWeight={600}>
                  {props.casting?.filter(Distinct)?.join(", ")}
                </Typography>
              </Typography>
            )}
            <Typography component="p" sx={{ mb: 1.5 }}>
              {props.description}
            </Typography>
            {props.tags && (
              <Stack direction="row" spacing={1}>
                {props.tags.map((tag) => (
                  <Chip size="small" label={tag} key={tag} />
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Container>
    </Hero>
  );
};

const Hero = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "100vh",
  maxHeight: "700px",
  display: "flex",
  alignItems: "stretch",
  ".MuiContainer-root": {
    position: "relative",
    display: "flex",
  },
}));

const Background = styled(Box)(({ theme }) => ({
  position: "absolute",
  height: "100%",
  overflow: "hidden",
  width: "100%",
  background: theme.palette.mode === "light" ? theme.palette.background.paper : "",

  "&:after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(180deg, rgba(${hexToRgbString(
      theme.palette.background.default
    )}, 0) 0%, rgba(${hexToRgbString(theme.palette.background.default)}, 0.40) 38.24%, ${
      theme.palette.background.default
    } 88.03%), radial-gradient(237.49% 141.42% at 100.00% -0.00%, rgba(${hexToRgbString(
      theme.palette.background.default
    )}, 0.00) 0%, rgba(${hexToRgbString(theme.palette.background.default)}, 0.00) 45.17%, rgba(${hexToRgbString(
      theme.palette.background.default
    )}, 0.50) 59.44%, ${theme.palette.background.default} 72.80%)`,
    // background: `linear-gradient(180deg, rgba(${hexToRgbString(theme.palette.background.default)}, 0) 20%, ${
    //   theme.palette.background.default
    // } 90%), radial-gradient(86.28% 385.52% at 100% 33.17%, rgba(${hexToRgbString(
    //   theme.palette.background.default
    // )}, 0) 0%, ${theme.palette.background.default} 100%)`,
  },
}));

export const TitleHeroStyled = {
  Hero,
  Background,
};

export default TitleHero;
